import { BtFileObject, response } from "./../models/index";
import { ApiClient } from "../network/ApiClient";
import { Document } from "../models";
import { ApiEndpoints } from "../network/ApiEndpoints";
import { BlockBlobClient } from "@azure/storage-blob";

export type FileRegistrationRequest = {
  fileOperationId: string;
  fileName: string;
  fileSize: number;
  fileFormat: string;
  isNewVersion?: boolean;
  rootDocumentId?: number;
};

export type FileRegistrationResult = {
  fileOperationId: string;
  sasUrl?: string;
  expiresOn?: string;
  errorMessage?: string;
};

type RegisterUploadApiResponse = {
  code?: number;
  message?: string;
  data?:
    | FileRegistrationResult[]
    | {
        items?: FileRegistrationResult[];
        uploads?: FileRegistrationResult[];
      };
};

class DocumentService {
  //Fetching top 2 files inside a folder for Project Card
  public async getAll(
    folderName: string
  ): Promise<{ code: number; message: string; data: Document[] }> {
    const encodedFolderName = encodeURIComponent(folderName);
    let response = await ApiClient.get<any>(
      ApiEndpoints.getDocumentsInFolder(),
      {
        projectId: encodedFolderName,
      },
      {}
    );
    return Promise.resolve(response);
  }

  //Fetching all documents inside a folder
  public async getAllDocs(folderName: string): Promise<Document[]> {
    const encodedFolderName = encodeURIComponent(folderName);
    let response = await ApiClient.get<any>(
      ApiEndpoints.getDocumentsInFolder(),
      {
        projectId: encodedFolderName,
      },
      {}
    );
    try {
      return await Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject("No documents found");
    }
  }

  private normalizeRegistrationResult(item: any): FileRegistrationResult {
    return {
      fileOperationId: item.fileOperationId ?? item.FileOperationId ?? "",
      sasUrl: item.sasUrl ?? item.SasUrl,
      expiresOn: item.expiresOn ?? item.ExpiresOn,
      errorMessage: item.errorMessage ?? item.ErrorMessage,
    };
  }

  public async registerUpload(
    files: FileRegistrationRequest[],
    projectId: number,
    rootDocumentId?: number,
    isNewVersion?: boolean,
  ): Promise<FileRegistrationResult[]> {
    const payload = { projectId, rootDocumentId, isNewVersion, files };
    const response = await ApiClient.post<typeof payload, RegisterUploadApiResponse | FileRegistrationResult[]>(
      ApiEndpoints.registerUpload(),
      payload
    );

    if (Array.isArray(response)) {
      return response.map((item) => this.normalizeRegistrationResult(item));
    }

    if (Array.isArray(response?.data)) {
      return (response.data as any[]).map((item) => this.normalizeRegistrationResult(item));
    }

    if (Array.isArray((response?.data as any)?.items)) {
      return ((response?.data as any)?.items || []).map((item: any) => this.normalizeRegistrationResult(item));
    }

    if (Array.isArray((response?.data as any)?.uploads)) {
      return ((response?.data as any)?.uploads || []).map((item: any) => this.normalizeRegistrationResult(item));
    }

    return [];
  }

  public async completeUpload(fileOperationId: string): Promise<any> {
    return ApiClient.post(ApiEndpoints.completeUpload(), { fileOperationId });
  }

  public async cancelUpload(fileOperationId: string): Promise<any> {
    return ApiClient.post(ApiEndpoints.cancelUpload(), { fileOperationId });
  }

  public async getUploadStatuses(fileOperationIds: string[]): Promise<any> {
    return ApiClient.post(ApiEndpoints.uploadStatuses(), { fileOperationIds });
  }

  public async uploadBlobToSas(
    sasUrl: string,
    file: File,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const blobClient = new BlockBlobClient(sasUrl);
    await blobClient.uploadData(file, {
      abortSignal: signal,
      blobHTTPHeaders: {
        blobContentType: file.type || "application/octet-stream",
      },
      onProgress: (evt) => {
        if (!onProgress || !file.size) return;
        const percent = Math.round((evt.loadedBytes / file.size) * 100);
        onProgress(percent);
      },
    });
  }

  public async upload(
    projectId: number,
    UploadedFiles: BtFileObject[]
  ): Promise<any> {
    const formData = new FormData();
    UploadedFiles.forEach((fileObject) => {
      if (
        fileObject.file.name.endsWith(".doc") ||
        fileObject.file.name.endsWith(".DOC")
      ) {
        let fileName =
          fileObject.fileName?.split(".doc")[0].split(".DOC")[0] + ".doc";
        formData.append("UploadedFiles", fileObject.file);
        formData.append("fileNames", fileName);
      } else {
        let fileName =
          fileObject.fileName
            ?.split(".docx")[0]
            .split(".DOCX")[0]
            .split(".DOCx")[0]
            .split(".doc") + ".docx";

        formData.append("UploadedFiles", fileObject.file);
        formData.append("fileNames", fileName);
      }
    });
    const response: response = await ApiClient.post(
      ApiEndpoints.uploadMultiFile(),
      formData,
      {
        projectId: projectId,
      },
      {
        "Content-Type": "multipart/form-data",
      }
    );
    return Promise.resolve(response);
  }

  public async uploadNewVersion(
    UploadedFiles: BtFileObject[],
    rootDocumentId: number
  ): Promise<any> {
    const formData = new FormData();
    UploadedFiles.forEach((file) => {
      formData.append("UploadedFile", file.file);
    });
    const response: response = await ApiClient.post(
      ApiEndpoints.uploadNewVersion(),
      formData,
      {
        rootDocumentId: rootDocumentId,
      },
      {
        "Content-Type": "multipart/form-data",
      }
    );
    return Promise.resolve(response);
  }

  public async update(id: number, payload: Document): Promise<Document> {
    return Promise.resolve({ ...payload, documentId: id } as Document);
  }

  public async delete(id: string): Promise<void> {
    return ApiClient.delete("api-endpoint", { documentId: id });
  }

  public async grantUserPermission(
    documentUrl: string | undefined,
    type: string | null
  ): Promise<any> {
    let response = await ApiClient.get(
      ApiEndpoints.grantUserPermission(),
      {
        documentUrl: documentUrl,
        type: type,
      },
      {}
    );
    return response;
  }
}

const documentService = new DocumentService();
export { documentService as DocumentService };
